import React, { useEffect, useRef } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import Avatar from "@mui/material/Avatar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { IBasePkgComparisonModalProps } from './pkg-comparison-modal-v2-module';
import DtvButton from "@directv-digital/dds.ui.dtv-button";

export interface IPackageComparisonModalV2Props extends IBasePkgComparisonModalProps {
  isStreamPackages?: boolean;
  useSourcePrices?: boolean;
  sourcePriceCopy?: string[];
  useSourceChannelCounts?: boolean;
  enableSourcePkgHighlighting?: boolean;
  sourceIndex?: number;
}

const errorBlock = () => (
    <DtvBox sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    }}>
      <DtvText
        content="We're having trouble loading package data. Please try again later"
        sx={{
 fontSize: "42px",
fontFamily: "Roboto",
fontWeight: 600
}}
      />
    </DtvBox>
)

const colorMap = {
  dividerGray: "#D1D1D1",
  white: "#FFF",
  jet: "jet.main",
  checkGreen: "#2AA277",
  dashGray: "#747474"
}

export function PkgComparisonModalV2(props: IPackageComparisonModalV2Props) {
  try {
    const {
      id,
      toolTitle,
      backgroundColor,
      columns,
      rows,
      isStreamPackages,
      enableSourcePkgHighlighting,
      sourceIndex,
      useSourcePrices,
      sourcePrices,
      useSourceChannelCounts,
      sourceChannelCounts,
      sourceHasNoLocals
    } = props;

    console.log(sourcePrices)

    const modifiedToolTitle = toolTitle?.content ? (
      isStreamPackages ?
        toolTitle?.content?.replace?.(/satellite/i, "Internet") || "DIRECTV Internet package features" :
        toolTitle?.content?.replace?.(/internet/i, "Satellite") || "DIRECTV Satellite package features"
    ) : (
      isStreamPackages ?
        "DIRECTV Internet package features" :
        "DIRECTV Satellite package features"
    )

    const getCellColor = (rowIx:number, colIx:number) => ((rowIx === 0 || colIx === 0) ? backgroundColor?.[colIx]?.color : 'unset');

    const getTableIcon = (type: string, srcPath: string, altName: string) => {
      try {
        return type && srcPath ? (
          <DtvBox sx={{
            borderRadius: "50%",
            backgroundColor: "transparent",
            margin: (type === "icon" || type === "channel-icon") ? "2px" : "auto",
            ...(type === "icon" && {
              height: "28px",
width: "28px"
            }),
            ...(type === "channel-icon" && {
              height: "36px",
width: "36px",
              boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.16)"
            })
          }}>
            <Avatar
              alt={altName}
              src={srcPath.startsWith("/dtvassets") ? `https://www.directv.com${srcPath}` : srcPath}
              sx={{
                ...(type === "checkmark" && {
                  width: "28px",
height: "28px",
                  bgcolor: "#2AA277"
                }),
                ...(type === "dash" && {
                  width: "28px",
height: "28px"
                }),
                ...(type === "icon" && {
                  height: "28px",
width: "28px"
                }),
                ...(type === "channel-icon" && {
                  height: "calc(36px / sqrt(2))",
                  width: "calc(36px / sqrt(2))",
                  position: "relative",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)"
                })
              }}
            />
          </DtvBox>
        ) : <></>;
      } catch {
        return <></>
      }
    }

    const highlightPkgCellIndex = enableSourcePkgHighlighting &&
      sourceIndex !== undefined && sourceIndex >= 0 ?
      (sourceIndex + 1) :
      -1;

    const modalRef = useRef();
    useEffect(() => {
      if (modalRef?.current && highlightPkgCellIndex > 0) {
        const scrollElem = (modalRef.current as HTMLElement)?.parentElement;
        // eslint-disable-next-line eqeqeq
        if (highlightPkgCellIndex == 1 || scrollElem?.scrollWidth == scrollElem?.offsetWidth) {
          return;
        }
        const colElem = ((modalRef.current as HTMLElement)?.querySelector(`[id=pkg-comparison-hcol-${highlightPkgCellIndex}]`) as HTMLElement);
        if (scrollElem && (colElem.offsetLeft + colElem.offsetWidth > scrollElem.offsetWidth)) {
          const maxScroll = scrollElem.scrollWidth - scrollElem.clientWidth + 1;
          //    scrollPos = offsetRight of the table)                  - clientWidth            + spacing
          const scrollPos = (colElem.offsetLeft + colElem.offsetWidth) - scrollElem.clientWidth + 10;
          scrollElem.scrollTo({
            left: Math.min(maxScroll, scrollPos),
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    }, []);

    try {
      return (
        <DtvBox id={id} ref={modalRef} sx={{
 height: "100%",
width: "100%"
}}>
          {toolTitle && (
            <DtvBox sx={{
              textAlign: "left",
              position: "fixed",
              top: {
                xs: "40px",
                sm: "44px",
                md: "56px"
              }
              // pseudoMarginBottom --> xs:16, sm:20 md: 20
            }}>
              <DtvText
                  component={toolTitle?.component || "div"}
                  content={modifiedToolTitle}
                  variant={"subhead1medium"}
                  sx={{
                    width: "100%",
                    paddingLeft: {
 xs: "20px",
sm: 0
},
                    fontSize: {
 xs: "20px",
sm: "30px",
md: "36px"
},
                    lineHeight: {
 xs: "24px",
sm: "36px",
md: "43px"
},
                    letterSpacing: {
 xs: "-0.48px",
sm: "-0.6px",
md: "-0.72px"
},
                    color: colorMap.jet,
                    fontWeight: 500,
                    ...toolTitle?.sx
                  }}
                />
            </DtvBox>
          )}
          <TableContainer
            sx={{
              backgroundColor: "inherit",
              overflowX: "visible",
              boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.08)"
            }}>
            <Table
              id={`${id}-table`}
              aria-label={isStreamPackages ?
                "DIRECTV Internet package comparison table" :
                "DIRECTV Satellite package comparison table"
              }
              sx={{
                position: "relative",
                borderSpacing: "0px",
                borderCollapse: "separate"
              }}>
              <TableHead>
                <TableRow>
                  {columns?.map((column, columnIndex, arr) => {
                    const { key, productName } = column;
                    // eslint-disable-next-line eqeqeq
                    const isFirstCol = columnIndex == 0;
                    // eslint-disable-next-line eqeqeq
                    const isLastCol = columnIndex == arr?.length - 1;
                    return (
                      <TableCell // acts as white background box to cover any cells scrolling behind
                        id={`pkg-comparison-hcol-${columnIndex}`}
                        key={`${columnIndex}-${key}`}
                        sx={{
                          backgroundColor: "#FFF",
                          height: "73px",
                          padding: 0,
                          border: "none",
                          position: "sticky",
                          ...(isFirstCol && {
                            top: 0,
                            left: 0,
                            zIndex: 20,
                            minWidth: "115px",
                            maxWidth: "115px"
                          }),
                          ...(!isFirstCol && {
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            width: "calc((100% - 115px) / 4)",
                            minWidth: "168px"
                          }),
                          ...column?.sx
                        }}>
                          <DtvBox
                            sx={{
                              height: "100%",
                              width: "100%",
                              display: "flex",
                              justifyContent: isFirstCol ? "flex-start" : "center",
                              alignItems: "center",
                              backgroundColor: backgroundColor?.[columnIndex]?.color || colorMap.jet,
                              borderRadius: "8px 8px 0 0",
                              border: "none",
                              ...(isFirstCol && {
                                padding: "4px 12px",
                                borderBottom: `2px solid ${colorMap.white}`
                              }),
                              ...(!isFirstCol && {
                                borderBottom: `1px solid ${
                                  highlightPkgCellIndex > 0 && highlightPkgCellIndex === columnIndex ?
                                  backgroundColor?.[columnIndex]?.color :
                                  colorMap.dividerGray}`,
                                borderLeft: `1px solid ${colorMap.white}`
                              }),
                              ...(!isLastCol && {
                                borderRight: `1px solid ${colorMap.white}`
                              })
                            }}>
                              <DtvText
                                component={productName?.component || "div"}
                                cta={column?.cta}
                                tooltip={column?.tooltip}
                                variant={isFirstCol ? "body1medium" : "subhead2medium"}
                                content={
                                  productName?.content.includes("CHOICE") ? "CHOICE™" :
                                  productName?.content.includes("PREMIER") ? "PREMIER™" :
                                  productName?.content
                                }
                                sx={{
                                  color: colorMap.white,
                                  ...(isFirstCol ? {
                                    lineHeight: "20.8px !important",
                                    fontSize: "16px !important",
                                    textAlign: "left"
                                  } : {
                                    fontFamily: "PFDinMed !important",
                                    lineHeight: "26px !important",
                                    fontSize: "20px !important",
                                    textAlign: "center",
                                    pt: "2px"
                                  }),
                                  ...column?.productName?.sx
                                }}
                              />
                          </DtvBox>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows?.map((row, rowIndex) => {
                  const { cells } = row;
                  const isLocalChannelsRow = cells?.[0]?.title?.content?.toLowerCase().includes("local channels");
                  return (
                  <TableRow key={rowIndex} sx={{
 fontFamily: "Roboto",
color: colorMap.jet
}}>
                    {/* eslint-disable-next-line complexity */}
                    {cells?.map((cell, cellIndex, arr) => {
                      const isFirstCol = cellIndex === 0;
                      const isLastCol = cellIndex === arr?.length - 1;
                      if (cell?.iconSet && !Array.isArray(cell?.iconSet)) {
 cell.iconSet = [cell.iconSet];
}
                      if (cell?.channelIconSet && !Array.isArray(cell?.channelIconSet)) {
 cell.channelIconSet = [cell.channelIconSet];
}

                      return (
                        <TableCell
                          id={`cell-${rowIndex}--${cellIndex}`}
                          key={`${rowIndex}--${cellIndex}`}
                          sx={{
                            overflow: "hidden",
                            border: "none",
                            padding: "4px 12px",
                            ...(!isLastCol && {
                              // eslint-disable-next-line eqeqeq
                              borderRight: `1px solid ${cellIndex == 0 && highlightPkgCellIndex == 1 ?
                                  backgroundColor?.[1]?.color :
                                highlightPkgCellIndex === cellIndex ?
                                  backgroundColor?.[cellIndex]?.color :
                                colorMap.dividerGray
                              }`
                            }),
                            ...(isLastCol && {
                              boxShadow: {
                                xs: highlightPkgCellIndex === cellIndex ? `-1px 0 0 ${backgroundColor?.[cellIndex]?.color} inset` : 'none',
                                sm: "0px 2px 10px 0px rgba(0, 0, 0, 0.08);"
                              },
                              borderRight: {
                                xs: 'none',
                                sm: highlightPkgCellIndex === cellIndex ? `1px solid ${backgroundColor?.[cellIndex]?.color}` : 'none'
                              }
                            }),
                            ...(isFirstCol && {
                              minWidth: "115px",
                              maxWidth: "115px",
                              position: "sticky",
                              left: 0,
                              zIndex: 10,
                              backgroundColor: getCellColor(rowIndex, cellIndex),
                              borderBottom: `${cells[cellIndex + 1]?.channelDetails?.content ? "1px" : "2px"} solid ${colorMap.white}`
                            }),
                            ...(!isFirstCol && {
                              width: "calc((100% - 115px) / 4)",
                              minWidth: "168px",
                              zIndex: 5,
                              backgroundColor: colorMap.white,
                              borderLeft: `1px solid ${highlightPkgCellIndex === cellIndex ?
                                backgroundColor?.[cellIndex]?.color :
                                colorMap.dividerGray}`,
                              borderBottom: `1px solid ${highlightPkgCellIndex === cellIndex ?
                                backgroundColor?.[cellIndex]?.color :
                                colorMap.dividerGray}`
                            }),

                            ...(cell?.available && {
                              height: "62px"
                            }),
                            ...(cell?.channelCount && {
                              height: "29px"
                            }),
                            verticalAlign:
                              cell?.price?.content ||
                              cell?.channelDetails ||
                              cell?.sportsChannelDetails ||
                              (cell?.iconSet || cell?.textAboveIcons || cell?.textBelowIcons) ?
                                "top" :
                                "middle"
                        }}>
                          <DtvBox
                            id={`content-ctnr-${rowIndex}--${cellIndex}`}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              textAlign: "left",
                              flexDirection: "column",
                              "& * > ul": {
                                paddingLeft: "20px",
                                marginTop: "4px"
                              }
                            }}>
                              {/** Row headers at col 0 */}
                              {cell?.title?.content ? (
                                <DtvText
                                  component={cell?.title?.component || "div"}
                                  tooltip={cell?.tooltip}
                                  variant="body3"
                                  content={
                                    (cell?.title?.content.toLowerCase().includes("local channels")) ?
                                      `Local channels ${isStreamPackages ? "included" : "available"}` :
                                    cell?.title?.content
                                  }
                                  sx={{
                                    color: colorMap.white,
                                    fontSize: "14px !important",
                                    lineHeight: "18.2px",
                                    fontWeight: 400,
                                    ...cell?.title?.sx
                                  }}
                                />
                              ) :
                              cell?.price?.content ? (
                                <>
                                  <DtvText
                                    component={cell?.price?.component || "div"}
                                    cta={cell?.price?.cta}
                                    tooltip={cell?.price?.tooltip}
                                    variant="body1medium"
                                    content={
                                      useSourcePrices && sourcePrices?.[cellIndex - 1] !== undefined ?
                                        sourcePrices[cellIndex - 1]
                                            ?.substring(0, sourcePrices?.[cellIndex - 1].indexOf('/mo.') + 4) :
                                        cell?.price?.content
                                    }
                                    sx={{
                                      fontSize: "16px !important",
                                      lineHeight: "20.8px",
                                      fontWeight: 500,
                                      ...cell?.price?.sx
                                    }}
                                  />
                                  <DtvText
                                    component={cell?.component || "div"}
                                    cta={cell?.priceDescription?.cta}
                                    tooltip={cell?.priceDescription?.tooltip}
                                    variant="smalltext"
                                    content={
                                      cell?.priceDescription?.content
                                      // Feature discontinued
                                      // useSourcePrices && props?.sourcePriceCopy && props?.sourcePriceCopy?.[cellIndex-1]
                                      //   ? props?.sourcePriceCopy[cellIndex-1]
                                      //   : cell?.priceDescription?.content
                                    }
                                    sx={{
                                      fontSize: "12px !important",
                                      lineHeight: "130%",
                                      fontWeight: 400,
                                      ...cell?.priceDescription?.sx
                                    }}
                                  />
                                </>
                              ) :
                              cell?.channelCount ? (
                                <DtvText
                                  component={"div"}
                                  variant="body1medium"
                                  content={
                                    useSourceChannelCounts && sourceChannelCounts && sourceChannelCounts?.[cellIndex - 1] !== '' ?
                                      sourceChannelCounts?.[cellIndex - 1] :
                                      cell?.channelCount
                                  }
                                  sx={{
                                    fontSize: "16px !important",
                                    lineHeight: "20.8px",
                                    fontWeight: 500,
                                    textAlign: "center"
                                  }}
                                />
                              ) :
                              cell?.channelDetails?.content ? (
                                <DtvText
                                  component={cell?.channelDetails?.component || "div"}
                                  content={cell?.channelDetails?.content}
                                  variant="smalltext"
                                  sx={{
                                    py: /* 4px +*/ "8px",
                                    fontSize: {
                                      xs: cell?.fontSizeXS || "12px",
                                      sm: cell?.fontSizeSM || "12px",
                                      md: cell?.fontSizeMD || "12px"
                                    },
                                    lineHeight: "15.6px",
                                    fontWeight: 400,
                                    "& b": {
                                      fontWeight: 700,
                                      lineHeight: "15.6px"
                                    },
                                    ...cell?.channelDetails?.sx
                                  }}
                                />
                              ) :
                              cell?.sportsChannelDetails?.content ? (
                                <DtvText
                                  component={cell?.sportsChannelDetails?.component || "div"}
                                  content={cell?.sportsChannelDetails?.content}
                                  variant="smalltext"
                                  sx={{
                                    fontSize: {
                                      xs: cell?.fontSizeXS || "12px",
                                      sm: cell?.fontSizeSM || "12px",
                                      md: cell?.fontSizeMD || "12px"
                                    },
                                    lineHeight: "15.6px",
                                    fontWeight: 400,
                                    "& b": {
                                      fontWeight: 700,
                                      lineHeight: "15.6px"
                                    },
                                    ...cell?.sportsChannelDetails?.sx
                                  }}
                                />
                              ) :
                              (cell?.iconSet?.length > 0 || cell?.textAboveIcons?.content || cell?.textBelowIcons?.content) ? (
                                <>
                                  {cell?.textAboveIcons?.content && (
                                    <DtvText
                                      component={cell?.textAboveIcons?.component || "div"}
                                      content={cell?.textAboveIcons?.content}
                                      variant="smalltext"
                                      sx={{
                                        fontSize: "12px !important",
                                        lineHeight: "15.6px",
                                        fontWeight: 400,
                                        ...cell?.textAboveIcons?.sx
                                      }}
                                    />
                                  )}
                                  {cell?.iconSet && (
                                    <DtvBox
                                      id={`atGlance-iconSet-${cellIndex}`}
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        flexWrap: "wrap",
                                        mx: { // offsets padding to allow full width before wrapping
                                          xs: "-1px",
sm: "-2px",
md: "-6px"
                                        },
                                        mb: "2px"
                                      }}>
                                        {cell?.iconSet?.map((_, ix) => {
                                            // eslint-disable-next-line eqeqeq
                                            if (ix % 2 == 1) {
 return null;
}
                                            return (
                                                <DtvBox key={ix} sx={{ display: "flex" }}>
                                                  {getTableIcon(
                                                    "icon",
                                                    cell?.iconSet[ix]?.src,
                                                    cell?.iconSet[ix]?.name
                                                  )}
                                                  {(ix + 1 < cell?.iconSet?.length) &&
                                                    getTableIcon(
                                                      "icon",
                                                      cell?.iconSet[ix + 1]?.src,
                                                      cell?.iconSet[ix + 1]?.name
                                                    )
                                                  }
                                                </DtvBox>
                                            );
                                        })}
                                    </DtvBox>
                                  )}
                                  {cell?.textBelowIcons?.content && (
                                    <DtvText
                                      component={cell?.textBelowIcons?.component || "div"}
                                      content={cell?.textBelowIcons?.content}
                                      variant="smalltext"
                                      sx={{
                                        fontSize: "12px !important",
                                        lineHeight: "15.6px",
                                        fontWeight: 400,
                                        ...cell?.textBelowIcons?.sx
                                      }}
                                    />
                                  )}
                                </>
                              ) :
                              cell?.available && columns ? (
                                <>
                                  {/* Always show dash if on no locals toggle */}
                                  {(isLocalChannelsRow && sourceHasNoLocals) || cell?.available !== "YES_GREEN" ?
                                    getTableIcon(
                                      "dash",
                                      "/dtvassets/global/icons/2022/Control-Minus-gray.svg",
                                      (isLocalChannelsRow && sourceHasNoLocals) ?
                                        "Your plan card selection does not include local channels" :
                                        cell?.altText || `Not included in ${columns[cellIndex]?.productName?.content} package`
                                    ) :
                                    getTableIcon(
                                      "checkmark",
                                      "/dtvassets/global/icons/component-controls/legend_check.svg",
                                      cell?.altText || `Included in ${columns[cellIndex]?.productName?.content} package`
                                    )

                                  }
                                </>
                              ) :
                              (cell?.textAboveChannelIcons?.content || cell?.channelIconSet?.length > 0 || cell?.textBelowChannelIcons?.length > 0) ? (
                                <>
                                  {cell?.textAboveChannelIcons?.content && (
                                    <DtvText
                                      component={cell?.textAboveChannelIcons?.component || "div"}
                                      content={cell?.textAboveChannelIcons?.content}
                                      variant="smalltext"
                                      sx={{
                                        fontSize: "12px !important",
                                        lineHeight: "15.6px",
                                        fontWeight: 400,
                                        textAlign: "center",
                                        mb: "2px",
                                        ...cell?.textAboveChannelIcons?.sx
                                      }}
                                    />
                                  )}
                                  {cell?.channelIconSet?.length > 0 && (
                                    <DtvBox
                                      id={`channelIconSet-${cellIndex}`}
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        flexWrap: "wrap",
                                        margin: { // offsets padding for full width before wrapping
                                          xs: "0 -1px",
                                          sm: "0 -2px",
                                          md: "0 -6px"
                                        }
                                      }}>
                                      {cell?.channelIconSet?.map((_, ix) => {
                                          // eslint-disable-next-line eqeqeq
                                          if (ix % 2 == 1) {
 return null;
}
                                          return (
                                              <DtvBox
                                                key={ix}
                                                sx={{ display: "flex" }}>
                                                  {getTableIcon(
                                                    "channel-icon",
                                                    cell?.channelIconSet[ix]?.src,
                                                    cell?.channelIconSet[ix]?.name
                                                  )}
                                                  {(ix + 1 < cell.channelIconSet?.length) &&
                                                    getTableIcon(
                                                      "channel-icon",
                                                      cell?.channelIconSet[ix + 1]?.src,
                                                      cell?.channelIconSet[ix + 1]?.name
                                                    )
                                                  }
                                              </DtvBox>
                                          );
                                      })}
                                    </DtvBox>
                                  )}
                                  {cell?.textBelowChannelIcons?.content && (
                                    <DtvText
                                      component={cell?.textBelowChannelIcons?.component || "div"}
                                      content={cell?.textBelowChannelIcons?.content}
                                      variant="smalltext"
                                      sx={{
                                        fontSize: "12px !important",
                                        lineHeight: "15.6px",
                                        fontWeight: 400,
                                        textAlign: "center",
                                        mt: "2px",
                                        ...cell?.textBelowChannelIcons?.sx
                                      }}
                                    />
                                  )}
                                </>
                              ) :
                              (cell?.ctaPanel && Array.isArray(cell?.ctaPanel?.ctaList)) ?
                                <DtvBox sx={{
                                  display: "flex",
flexDirection: "column",
alignItems: "center",
                                  gap: {
 xs: "10px",
sm: "16px"
}
                                }}>
                                  {cell?.ctaPanel?.ctaList?.map((buttonProps, ix) => (
                                      <DtvBox key={cellIndex} sx={{
                                        display: "flex",
flexBasis: "100%",
justifyContent: "center",
                                        textAlign: "center"
                                      }}>
                                        <DtvButton
                                          id={`${columns[cellIndex]?.key}-pkg-cta-${ix}`}
                                          key={buttonProps?.content}
                                          mode={buttonProps?.mode || "dark"}
                                          target={buttonProps?.target || "_self"}
                                          variant={buttonProps?.variant || "uxprimary"}
                                          aria-label={buttonProps?.content ||
                                            `Shop ${columns[cellIndex]?.productName?.content}`
                                          }
                                          label={buttonProps?.conten ||
                                            `Shop ${columns[cellIndex]?.productName?.content}`}
                                          sx={{
                                            padding: "10px 30px",
                                            width: "auto",
                                            fontSize: {
 xs: "12px",
sm: "14px"
}
                                          }}
                                          targeturl={buttonProps?.targeturl ||
                                            `https://streamtv.directv.com/shop/${columns[cellIndex]?.key.toLowerCase()}-package`}
                                        />
                                      </DtvBox>
                                    ))}
                                </DtvBox> :
                              (
                                null
                              )}
                          </DtvBox>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  );
                })}
                <TableRow>
                  {columns?.map((column, columnIndex, arr) => {
                    const isFirstCol = columnIndex === 0;
                    const isLastCol = columnIndex === arr?.length - 1;
                    return (
                      <TableCell
                        key={column?.key || columnIndex}
                        sx={{
                          backgroundColor: backgroundColor?.[columnIndex]?.color || colorMap.jet,
                          height: "18px",
                          padding: 0,
                          border: "none",
                          borderRadius: "0 0 8px 8px",
                          ...(isFirstCol && {
                            minWidth: "115px",
                            width: {
 xs: "17%",
sm: "16%"
},
                            zIndex: 20,
                            position: "sticky",
                            left: 0,
                            // borderTop: `2px solid ${colorMap.white}`,
                            borderRight: `1px solid ${colorMap.dividerGray}`
                          }),
                          ...(!isFirstCol && {
                            minWidth: "168px",
                            width: {
 xs: "17%",
sm: "16%"
},
                            border: `1px ${!isLastCol ? "1px" : "0"} 1px 1px solid ${colorMap.dividerGray}`
                          })
                        }}
                      ></TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DtvBox>
      );
    } catch (error) {
      console.error("Package Comparison Modal error", error);
      return errorBlock();
    }
  } catch (error) {
    console.error("FATAL Package Comparison Modal error", error);
    return errorBlock();
  }
};
